import { useEffect, useState } from 'react';
import { History, DollarSign } from 'lucide-react';
import { supabase } from '../../supabaseClient';

const FIELD_LABEL = {
  cost_cents: 'Cost',
  price_cents: 'Price',
  owner: 'Owner',
  status: 'Status',
  quantity_available: 'Quantity',
  name: 'Name',
  description: 'Description',
  category_id: 'Category',
};

const OWNER_LABEL = { trainer_center: 'Trainer Center', olin: 'Olin' };

const show = (field, value) => {
  if (value === null || value === undefined || value === '') return '—';
  if (field?.endsWith('_cents')) return `$${(Number(value) / 100).toFixed(2)}`;
  if (field === 'owner') return OWNER_LABEL[value] || value;
  if (String(value).length > 60) return `${String(value).slice(0, 60)}…`;
  return String(value);
};

const stamp = (iso) =>
  new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });

// Every change to this product, who made it and when. Written by a database
// trigger rather than by this app, so an edit made straight through the Supabase
// dashboard shows up here the same as one made on this screen.
export default function ProductHistory({ productId }) {
  const [rows, setRows] = useState(null);
  const [moneyOnly, setMoneyOnly] = useState(false);

  useEffect(() => {
    if (!productId) return;
    (async () => {
      const { data } = await supabase
        .from('sv_audit_log')
        .select('id, at, actor_email, actor_role, action, field, old_value, new_value, money_field')
        .eq('table_name', 'products')
        .eq('record_id', productId)
        .order('at', { ascending: false })
        .limit(200);
      setRows(data || []);
    })();
  }, [productId]);

  if (!productId) return null;

  const visible = (rows || []).filter((r) => !moneyOnly || r.money_field);

  return (
    <div className="sv-card" style={{ padding: 20, marginTop: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <History size={17} />
          <h2 style={{ fontSize: '1rem', fontWeight: 800, margin: 0 }}>History</h2>
        </div>
        <button
          type="button"
          className={moneyOnly ? 'sv-btn' : 'sv-btn-outline'}
          onClick={() => setMoneyOnly((v) => !v)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', fontSize: '.82rem' }}
        >
          <DollarSign size={14} /> Money changes only
        </button>
      </div>

      {rows === null ? (
        <p style={{ margin: 0, color: 'var(--text-muted)' }}>Loading…</p>
      ) : visible.length === 0 ? (
        <p style={{ margin: 0, color: 'var(--text-muted)' }}>
          {moneyOnly ? 'No cost, price, or owner changes recorded.' : 'No changes recorded yet.'}
        </p>
      ) : (
        <div style={{ display: 'grid', gap: 2 }}>
          {visible.map((r) => (
            <div
              key={r.id}
              style={{
                display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'baseline',
                padding: '10px 12px', borderRadius: 8,
                background: r.money_field ? 'var(--surface-2, rgba(127,127,127,.07))' : 'transparent',
              }}
            >
              <span style={{ fontSize: '.78rem', color: 'var(--text-muted)', minWidth: 150 }}>{stamp(r.at)}</span>
              <span style={{ fontSize: '.88rem', fontWeight: 700, minWidth: 170 }}>
                {r.actor_email || (r.actor_role === 'service_role' ? 'System' : r.actor_role || 'Unknown')}
              </span>
              <span style={{ fontSize: '.88rem' }}>
                {r.action === 'insert' ? (
                  'Added this product'
                ) : r.action === 'delete' ? (
                  'Deleted this product'
                ) : (
                  <>
                    <b>{FIELD_LABEL[r.field] || r.field}</b>{' '}
                    {show(r.field, r.old_value)} <span style={{ color: 'var(--text-muted)' }}>→</span>{' '}
                    <b>{show(r.field, r.new_value)}</b>
                  </>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
